import * as React from 'react';
import { translate } from 'components/translate-hoc';
import PlatformList from '../platform-list';
import RedeemCodeForm from '../../../redeem-code/redeem-code.component';
import RedemptionMainError from '../redemption-platform-error';

import { getPlatformFromURL } from '../../redemption.utils';

import { IPlatformFirstPlatformSelectionProps } from './platform-first-platform-selection.types';
import { IProfilePlatform } from 'types/profiles';

import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';
import { CODE_URL_PARAMETER, TEXT_KEYS } from '../redemption-platform-selection.constants';
import '../redemption-platform-selection.styles.less';
import URLParams from 'utils/url-params';

export class PlatformFirstPlatformSelection extends React.Component<
IPlatformFirstPlatformSelectionProps,
IPlatformFirstPlatformSelectionState
> {
    public readonly platformIds: PLATFORM_TYPES[];
    public readonly platformIdFromURL: PLATFORM_TYPES | null;

    constructor(props: IPlatformFirstPlatformSelectionProps) {
        super(props);

        this.platformIdFromURL = this.getPlatformIDFromURL();
        this.platformIds = this.preparePlatformIds();

        this.state = {
            code: '',
            isCodeValid: false,
            activeProfilePlatform: this.getProfilePlatformById(this.props.preselectedPlatformId || this.getDefaultPlatformId()),
            showError: false
        };
    }

    public preparePlatformIds(): PLATFORM_TYPES[] {
        return this.platformIdFromURL
            ? [this.platformIdFromURL]
            : this.props.profilePlatforms.map(
                (profilePlatform: IProfilePlatform) => profilePlatform.id
            );
    }

    public componentDidMount() {
        // trigger platform linking popup when needed
        if (this.platformIdFromURL) {
            const platform = this.getProfilePlatformById(PLATFORM_TYPES[this.platformIdFromURL]);
            this.selectPlatform(platform);
        }

        const codeFromURL = URLParams().get(CODE_URL_PARAMETER);

        if (codeFromURL) {
            this.setState({ code: codeFromURL });
        }
    }

    public getPlatformIDFromURL(): PLATFORM_TYPES | null {
        const { profilePlatforms } = this.props;
        const platformId = getPlatformFromURL();

        if (!platformId) {
            return null;
        }

        // Make sure the one we parsed from URL is valid
        const platform = profilePlatforms.find(
            (profilePlatform: IProfilePlatform) => profilePlatform.id === platformId
        ) || null;

        return platform && platform.id;
    }

    public getDefaultPlatformId(): string | null {
        return this.isOnlyPCAvailable() ? this.platformIds[0] : null;
    }

    public getProfilePlatformById(platformId: PLATFORM_TYPES | null): IProfilePlatform | null {
        if (!platformId) {
            return null;
        }

        return this.props.profilePlatforms.find(
            (profilePlatform: IProfilePlatform) => profilePlatform.id === platformId
        ) || null;
    }

    public isOnlyPCAvailable(): boolean {
        if (this.isSingleOption()) {
            return this.platformIds[0] === PLATFORM_TYPES.PC;
        }

        return false;
    }

    public isSingleOption(): boolean {
        return this.platformIds.length === 1;
    }

    public selectPlatform = (profilePlatform: IProfilePlatform | null): void => {
        this.setState({
            activeProfilePlatform: profilePlatform,
            showError: false
        });
    };

    public getMyGiftsHandler = (): void => {
        const { activeProfilePlatform } = this.state;
        this.setState({ showError: !activeProfilePlatform || !this.state.isCodeValid });

        if (activeProfilePlatform && this.state.isCodeValid) {
            const platformId = PLATFORM_TYPES[activeProfilePlatform.id];

            this.props.redeemGift({ platform: platformId, code: this.state.code });
        }
    };

    public updateRedeemCode = (params: { code: string, isValid: boolean }): void => {
        this.setState({ showError: false, code: params.code, isCodeValid: params.isValid });
    };

    public render() {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const { activeProfilePlatform } = this.state;

        const activePlatformId = activeProfilePlatform && activeProfilePlatform.id;
        const isSinglePlatformAvailable: boolean = this.isSingleOption();
        const titleText: string = isSinglePlatformAvailable
            ? translateKey(TEXT_KEYS.REDEEM_MAIN_PAGE_TITLE_SINGLE_PLATFORM)
            : translateKey(TEXT_KEYS.REDEEM_MAIN_PAGE_TITLE);

        const formTitle: string = translateKey(TEXT_KEYS.REDEEM_CODE_TITLE);
        const formMessage: string = translateKey(TEXT_KEYS.REDEEM_CODE_MESSAGE);

        return (
            <div className="sitegen-redemption-platform-selection">
                <div className="sitegen-redemption-platform-selection__title">
                    <p>{titleText}</p>
                </div>

                <PlatformList
                    platformsList={this.platformIds}
                    profilePlatforms={this.props.profilePlatforms}
                    activePlatformId={activePlatformId}
                    onSelectPlatform={this.selectPlatform}
                />

                <h2 className="sitegen-redeem-code-input__title">{formTitle}</h2>
                <p className="sitegen-redeem-code-input__message">{formMessage}</p>

                <RedeemCodeForm
                    fetching={this.props.fetching}
                    code={this.state.code}
                    codeFormat={this.props.codeFormat}
                    submitCode={this.getMyGiftsHandler}
                    updateCode={this.updateRedeemCode}
                />

                <RedemptionMainError
                    showError={this.state.showError}
                    activeProfilePlatform={activeProfilePlatform}
                    isCodeValid={this.state.isCodeValid}
                />
            </div>
        );
    }
}

interface IPlatformFirstPlatformSelectionState {
    readonly code: string;
    readonly isCodeValid: boolean;
    readonly showError: boolean;
    readonly activeProfilePlatform: IProfilePlatform | null;
}

export default translate()(PlatformFirstPlatformSelection);
